
import { GoogleGenAI, GenerateContentResponse, HarmCategory, HarmBlockThreshold } from "@google/genai";
import { TransformationConfig } from '../types';
import { POSE_LIBRARY } from '../poseData';

// --- MODEL MANAGEMENT & FALLBACK LOGIC ---

const PRIMARY_MODEL = 'gemini-3-pro-image-preview';
const FALLBACK_MODEL = 'gemini-2.5-flash-image';
const FALLBACK_DURATION_MS = 60 * 60 * 1000; // 1 Hour

const getActiveModel = (): string => {
    try {
        const fallbackTs = localStorage.getItem('gemini_fallback_ts');
        if (fallbackTs) {
            const elapsed = Date.now() - parseInt(fallbackTs, 10);
            if (elapsed < FALLBACK_DURATION_MS) {
                return FALLBACK_MODEL;
            } else {
                // Time expired, revert to primary
                localStorage.removeItem('gemini_fallback_ts');
                console.log("Fallback period expired. Reverting to primary model:", PRIMARY_MODEL);
            }
        }
    } catch (e) {
        // Ignore storage errors, default to primary
    }
    return PRIMARY_MODEL;
};

const triggerFallback = () => {
    try {
        // Only set if not already set, to preserve the original start time of the outage
        if (!localStorage.getItem('gemini_fallback_ts')) {
            console.warn(`⚠️ API Error detected. Switching to fallback model (${FALLBACK_MODEL}) for 1 hour.`);
            localStorage.setItem('gemini_fallback_ts', Date.now().toString());
        }
    } catch (e) {
        console.warn("Could not set fallback timestamp in localStorage (Quota Exceeded)");
    }
};

// Safe Environment Variable Accessor
const getEnv = (key: string) => {
  // Check Vite (import.meta.env)
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env) {
      // @ts-ignore
      if (import.meta.env[`VITE_${key}`]) return import.meta.env[`VITE_${key}`];
      // @ts-ignore
      if (import.meta.env[key]) return import.meta.env[key];
  }
  
  // Check Node/Standard (process.env) - safely wrapped
  try {
      // @ts-ignore
      if (typeof process !== 'undefined' && process.env && process.env[key]) {
          // @ts-ignore
          return process.env[key];
      }
  } catch (e) {
      // Ignore process error in browser
    }
  
  return undefined;
};

// Ensure API key is available
const API_KEY = getEnv('API_KEY') || 'AIzaSyAx-0wVV2X0JHpC1tJtFSjs8nbI2SyvH6Y';

if (!API_KEY || API_KEY.startsWith('AIzaSyAx')) {
  console.log("Using API Key configuration.");
}

const ai = new GoogleGenAI({ apiKey: API_KEY });

// --- RESOLUTION SCALER ---
type ImageResolution = '1K' | '2K' | '4K';

const getResolutionForPlan = (plan: string = 'trial', override?: '1K' | '2K' | '4K'): ImageResolution => {
    if (override) return override;
    if (plan === 'platinum') return '4K';
    if (plan === 'silver' || plan === 'gold' || plan === 'basic' || plan === 'pro' || plan === 'creator') return '2K';
    return '1K'; // trial/free
};

// --- WATERMARK HELPER UTILITIES ---
// KEPT FOR FUTURE USE BUT NOT ACTIVE ON TRIAL ANYMORE

const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(img); // Resolve anyway to avoid crash
        img.src = src;
    });
};

const drawOverlayBranding = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    // Large, semi-transparent overlay text
    const fontSize = Math.max(24, width * 0.08); 
    ctx.font = `900 ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const text = "RESHOOTER.COM";
    const x = width / 2;
    const y = height / 2;

    // Heavy shadow for readability on any background
    ctx.shadowColor = "rgba(0,0,0,0.8)";
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // Semi-transparent White
    ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
    ctx.fillText(text, x, y);

    // Clean up
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
};

// --- MAIN WATERMARK FUNCTION ---
const applyWatermark = async (base64Result: string, originalFile?: File): Promise<string> => {
    return new Promise(async (resolve) => {
        try {
            const resultImg = await loadImage(base64Result);
            // Ignore originalFile for layout purposes as per user request.
            // We just render the generated image with a watermark on top.

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) { resolve(base64Result); return; }

            // Set canvas to result image dimensions
            canvas.width = resultImg.width;
            canvas.height = resultImg.height;

            // Draw Result Image
            ctx.drawImage(resultImg, 0, 0);

            // Draw Overlay Branding (Watermark)
            drawOverlayBranding(ctx, canvas.width, canvas.height);

            resolve(canvas.toDataURL('image/png'));

        } catch (e) {
            console.error("Error applying watermark:", e);
            resolve(base64Result); // Fallback to raw result if canvas fails
        }
    });
};

const fileToPart = async (file: File): Promise<{ inlineData: { data: string; mimeType: string } }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1];
      
      // CRITICAL FIX: Ensure MIME Type is never empty
      let mimeType = file.type;
      if (!mimeType) {
          if (file.name.toLowerCase().endsWith('.png')) mimeType = 'image/png';
          else if (file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')) mimeType = 'image/jpeg';
          else mimeType = 'image/png'; // Default fallback to prevent API error
      }

      resolve({
        inlineData: {
          data: base64Data,
          mimeType: mimeType,
        },
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// --- ERROR NORMALIZATION HELPER ---
export const normalizeError = (error: any): Error => {
    if (error instanceof Error) return error;
    
    // Catch DOM Events that stringify to {"isTrusted":true}
    if (typeof error === 'object' && error !== null && (error as any).isTrusted) {
        return new Error("Network request failed. Please check your internet connection, firewall, or VPN.");
    }
    
    if (typeof error === 'string') return new Error(error);
    
    // Attempt to stringify, fallback to generic
    try {
        return new Error(JSON.stringify(error));
    } catch (e) {
        return new Error("An unexpected error occurred.");
    }
};

// Helper for Exponential Backoff with Model Switching
const retryWithBackoff = async <T>(
    fn: () => Promise<T>,
    retries = 3,
    baseDelay = 2000
): Promise<T> => {
    try {
        try {
            return await fn();
        } catch (localStorageError) {
             // If localStorage quota exceeded during execution, just proceed
             return await fn();
        }
    } catch (rawError: any) {
        // ENHANCED ERROR CHECKING FOR ROBUST FALLBACK
        const error = normalizeError(rawError);
        
        // Try to extract status code from original raw error if possible, else generic
        const statusCode = rawError?.status || rawError?.statusCode || rawError?.response?.status || rawError?.error?.code;
        const errorMessage = error.message;
        
        const isQuotaError = 
            statusCode === 429 || 
            errorMessage.includes('429') || 
            errorMessage.includes('Quota') || 
            errorMessage.includes('RESOURCE_EXHAUSTED');
            
        const isServerError = 
            statusCode === 503 || 
            statusCode === 500 || 
            errorMessage.includes('Internal Server Error') ||
            errorMessage.includes('Overloaded') ||
            errorMessage.includes('Network') || // Treat network failures as retryable
            errorMessage.includes('fetch');
        
        if (retries > 0 && (isQuotaError || isServerError)) {
            // Trigger Fallback
            triggerFallback();
            
            const waitTime = baseDelay * Math.pow(2, 3 - retries); // 2s, 4s, 8s
            const currentModel = getActiveModel(); // This will now return the fallback model
            
            console.warn(`API Error (${statusCode || 'Network'}). Switching/Retrying with ${currentModel} in ${waitTime}ms... (${retries} retries left)`);
            
            await new Promise(resolve => setTimeout(resolve, waitTime));
            return retryWithBackoff(fn, retries - 1, baseDelay);
        }
        
        // Pass the NORMALIZED error up the chain
        throw error;
    }
};

const getRandomPose = (category: string): string => {
    const poses = POSE_LIBRARY[category];
    if (!poses || poses.length === 0) return "";
    return poses[Math.floor(Math.random() * poses.length)];
};

const buildPrompt = (config: TransformationConfig, extraInstruction: string = '', assignedPose: string = '') => {
    let prompt = `
      You are the ReShooter AI, a world-class autonomous photography transformation engine.
      
      TASK:
      Transform the provided MAIN image (the first image) into a brand-new, award-winning photograph by COMPLETELY RECONSTRUCTING the lighting environment.
      
      CRITICAL RULES:
      1. KEEP the subject identity perfectly (same person/object).
      2. **MANDATORY LIGHTING RESET**: 
         - COMPLETELY DISCARD the original lighting direction, color, and quality.
         - You must hallucinate a brand new, high-end studio lighting setup from scratch.
         - **RELIGHTING IS NOT OPTIONAL.**
      3. **CRITICAL: PERFECT WHITE BALANCE**:
         - Eliminate all amateur yellow/blue/green color casts.
         - Ensure the white balance is neutral (approx. 5600K Daylight).
      4. QUALITY: Generate ultra-detailed, masterpiece quality.
      5. OUTPUT: Return ONLY the raw generated image.
      6. **CLEAN FRAME PROTOCOL (NO GEAR OR MODIFIERS)**: 
         - The output must be the FINAL PROCESSED PHOTOGRAPH, NOT a "behind-the-scenes" or "in-studio" shot.
         - **ABSOLUTELY NO LIGHTING EQUIPMENT OR MODIFIERS VISIBLE.**
         - There must be NO softboxes, NO umbrellas, NO light stands, NO scrims, NO flags, NO reflectors, NO cables, NO tripods, NO backdrop stands visible.
         - All light sources and modifiers must be strictly OFF-CAMERA and outside the viewable frame. 
         - If any part of a light modifier, light stand, or studio gear appears at the edges or corners, you have failed the assignment.
         - The background must be clean and free of all technical apparatus.
      7. **TOTAL RESHOOT PROTOCOL (ANTI-FILTER POLICY)**: 
         - This must NOT look like an edited iPhone photo or a filter applied to a low-res image.
         - You must RE-RENDER the scene from scratch using high-fidelity texture data.
         - Replace all digital noise, compression artifacts, and "smartphone sharpening" with organic optical quality.
         - The output must look like a $50,000 production, not a $0 app filter.
    `;

    // --- AUTO-ENHANCE DETECTION ---
    const isMinimalConfig = 
        config.backgroundMode === 'keep' && 
        config.propMode === 'keep' &&
        !config.cameraType && 
        !config.lighting && 
        !config.retouching && 
        (!config.framingMode || config.framingMode === 'keep') && 
        (!config.angleMode || config.angleMode === 'keep');

    if (isMinimalConfig) {
        prompt += `
        \n*** MANDATORY PROFESSIONAL UPGRADE ***
        The user has provided a raw input photo and wants a PROFESSIONAL STUDIO RESHOOT.
        
        EXECUTE THE FOLLOWING UPGRADES:
        1.  **LIGHTING OVERHAUL**: IGNORE original lighting. Apply "High-End Commercial Lighting" from scratch. Use softboxes, rim lights, and fill lights to create dimensionality. Banish flat lighting. IMPORTANT: Ensure the light sources and modifiers themselves are INVISIBLE and OFF-CAMERA. Do not render any lightbox, softbox, stand, or reflector in the frame.
        2.  **CAMERA FIDELITY**: Simulate the output of a Phase One XF IQ4 150MP. Extreme sharpness, perfect dynamic range, zero noise.
        3.  **AESTHETIC POLISH**: Color grade the image to look like a luxury editorial or high-budget advertisement. 
        4.  **WHITE BALANCE CORRECTION**: The final image MUST have perfect neutral white balance. Remove any "phone camera" yellow tint.
        
        The goal is to make the user ask "How did they shoot this?" 
        Make it look expensive and clean.
        `;
    }

    // --- CRITIQUE INJECTION (New Feature) ---
    if (config.critique) {
        prompt += `\n\nCRITIQUE BASED IMPROVEMENTS: The following issues were detected and MUST be fixed in the new image: "${config.critique}". Correct these defects while enhancing the overall quality.`;
    }

    // --- CAMERA GEAR LOGIC ---
    if (config.cameraType) {
        let cameraInstruction = "";
        if (config.cameraType.includes('Polaroid')) {
             cameraInstruction = "Shot on Polaroid SX-70. Instant film aesthetic, soft vintage focus, heavy chemical grain, creamy colors, slight vignetting, nostalgic mood.";
        } else if (config.cameraType.includes('Vivitar')) {
             cameraInstruction = "Shot on Vivitar Ultra Wide & Slim (VUWS). Plastic lens aesthetic, heavy vignetting, ultra-wide distortion, saturated colors, lo-fi toy camera charm.";
        } else if (config.cameraType.includes('Fujifilm')) {
             cameraInstruction = "Shot on Fujifilm X100VI. High-resolution digital sensor, classic chrome film simulation, sharp lens rendering, vivid colors, professional street photography aesthetic.";
        } else if (config.cameraType.includes('Leica')) {
             cameraInstruction = "Shot on Leica M11. Rangefinder aesthetic, iconic Leica color science, organic grain structure, sharp micro-contrast, depth and dimensionality. Natural color palette, balanced exposure, no excessive vignette.";
        }
        prompt += `\nCAMERA GEAR OVERRIDE: ${cameraInstruction} This is the PRIMARY visual style.`;
    } else if (!isMinimalConfig) {
        // DEFAULT (Only if not already covered by Auto-Enhance): High-End Leica M11 Logic
        const cameraInstruction = "Shot on Leica M11 with 50mm Summilux lens. 8K resolution. Extremely sharp focus on subject. Creamy, perfect bokeh background blur. Perfect white balance (neutral). Balanced exposure (no clipping). Natural, true-to-life colors. No vintage filters. No heavy vignette. Pure photographic perfection.";
        prompt += `\nCAMERA GEAR OVERRIDE: ${cameraInstruction} This is the PRIMARY visual style.`;
    }

    // --- RETOUCHING LOGIC ---
    if (config.retouching) {
        if (config.retouching === 'Light') {
            prompt += `\nRETOUCHING: Light. Subtly smooth skin texture and clean up stray hairs. Maintain natural look. Slight youthfulness.`;
        } else if (config.retouching === 'Moderate') {
            prompt += `\nRETOUCHING: Moderate. Polish skin texture, remove blemishes, neat hair styling. Subject should appear visibly younger and fresher.`;
        } else if (config.retouching === 'Extreme') {
            prompt += `\nRETOUCHING: Extreme. High-end magazine retouching. Flawless porcelain skin, perfect features, de-aged appearance (youthful glow). Idealized beauty standard.`;
        }
    } else {
        // Default subtle cleanup
        prompt += `\nRETOUCHING: Professional. Clean skin texture, perfect lighting on face, sharp eyes.`;
    }

    // --- POSE LOGIC ---
    if (config.poseDescription) {
        prompt += `\nSUBJECT POSE: ${config.poseDescription}. Keep subject identity but change body position/expression to match this.`;
    } else if (assignedPose) {
        prompt += `\nSUBJECT POSE: ${assignedPose}. Keep subject identity but change body position/expression to match this.`;
    }

    // --- LIGHTING LOGIC ---
    if (config.lighting) {
        prompt += `\nLIGHTING STYLE: ${config.lighting} lighting. Ensure all light sources and modifiers are strictly OFF-CAMERA and INVISIBLE. Completely replace original lighting with this new style.`;
    } else if (!isMinimalConfig) {
        // DEFAULT: Studio Strobe (Clean, Bright, Commercial)
        prompt += `\nLIGHTING STYLE: Professional Studio Strobe. Clean, bright, and even illumination. Soft, flattering shadows. Perfect neutral color temperature. No color casts. Ensure lighting equipment and modifiers are off-camera and invisible. Completely replace original lighting.`;
    }

    // --- BACKGROUND LOGIC ---
    if (config.backgroundMode === 'change') {
      prompt += `\nBACKGROUND INSTRUCTION: Change the background completely. Ensure the background is clean and devoid of any studio gear, light stands, or modifiers.`;
      if (config.backgroundRefImage) {
        prompt += ` Use the provided background reference image (the second image) as the PRIMARY guide. Match it exactly.`;
      }
      if (config.backgroundDescription) {
        prompt += ` Background description: "${config.backgroundDescription}".`;
      }
      if (config.backgroundColor) {
        prompt += ` Background color tone: ${config.backgroundColor}.`;
      }
    } else {
      prompt += `\nBACKGROUND INSTRUCTION: Keep the original background but upgrade the lighting and depth of field (bokeh). Remove any existing lighting gear from the background.`;
    }

    // --- PROP LOGIC ---
    if (config.propMode === 'change') {
      prompt += `\nPROP INSTRUCTION: Modify props.`;
      if (config.propRefImages && config.propRefImages.length > 0) {
        prompt += ` \nREFERENCE IMAGE USAGE: The provided prop reference images contain items. Transfer THESE items onto the subject. Apply them logically (e.g., holding, wearing, or placing near). IGNORE the person in the reference images.`;
      } else if (config.propRefImage) {
        prompt += ` \nREFERENCE IMAGE USAGE: The prop reference image contains an item. Transfer ONLY this item onto the subject. IGNORE the person in the reference image.`;
      }
      if (config.propDescription) {
        prompt += ` \nSpecific changes: ${config.propDescription}.`;
      }
    }

    // --- FRAMING LOGIC ---
    if (config.framingMode === 'change' && config.framing) {
      prompt += `\nFRAMING: ${config.framing}.`;
    } else {
      prompt += `\nFRAMING: Keep original framing but improve composition balance. Ensure perfect framing.`;
    }

    // --- ANGLE LOGIC ---
    if (config.angleMode === 'change' && config.angle) {
      prompt += `\nCAMERA ANGLE: ${config.angle}.`;
    } else {
      prompt += `\nCAMERA ANGLE: Keep original perspective.`;
    }

    if (extraInstruction) {
        prompt += `\nVARIATION INSTRUCTION: ${extraInstruction}`;
    }

    prompt += `\nFINAL QUALITY CHECK: Ensure 8K resolution, perfect white balance, and high dynamic range. Fix any exposure issues.
    NEGATIVE PROMPT: visible light source, light modifier, softbox in frame, light stand, corner equipment, studio gear, lightbox, light tent, shooting tent, white box, studio umbrella, ring light, lighting stand, tripod, studio equipment, ceiling light, lamp, bulb, flash reflection, original lighting, flat lighting, harsh shadows, flash photography, iPhone photography, samsung photography, computational photography, HDR filter, oversharpened, plastic skin, filter look, instagram filter, vsco preset, low res, digital noise, artifacts, blurry, distorted, backdrop stand, cables, power cords, reflectors, flags, scrims.`;
    
    return prompt;
};

// Helper to handle safety errors nicely
const handleGenerationCandidate = (candidate: any): string => {
    // Check Content
    if (candidate.content && candidate.content.parts) {
        for (const part of candidate.content.parts) {
            if (part.inlineData) {
                return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
            }
            if (part.text) {
                console.warn("AI returned text instead of image:", part.text);
                throw new Error(`AI Model Text Response: "${part.text.substring(0, 100)}..."`);
            }
        }
    }

    // Detailed Error Parsing for "Smarter Error Messages"
    if (candidate.finishReason) {
        const reason = candidate.finishReason;
        
        if (reason === "SAFETY" || reason === "IMAGE_SAFETY") {
            // Try to identify which rating triggered it
            let detail = "Safety Block";
            if (candidate.safetyRatings) {
                const blocked = candidate.safetyRatings.find((r: any) => r.blocked);
                if (blocked) {
                    detail = `Safety Block (${blocked.category})`;
                }
            }
            throw new Error(`${detail}: The AI flagged this request as potentially unsafe.`);
        }
        
        if (reason === "RECITATION") {
            throw new Error("Copyright Block: The AI recognized protected content and stopped.");
        }
        
        if (reason === "OTHER" || reason === "IMAGE_OTHER") {
            throw new Error("Processing Error (IMAGE_OTHER): The model encountered a temporary glitch. Please retry.");
        }
        
        if (reason !== "STOP") {
             throw new Error(`Generation Stopped: ${reason}. Please try a different photo.`);
        }
    }

    throw new Error("No valid image data found in response.");
};

export const generateReshoot = async (
  mainImage: File,
  config: TransformationConfig,
  plan: string = 'trial'
): Promise<string> => {
  try {
    const parts: any[] = [];
    parts.push(await fileToPart(mainImage));

    // STRICT CHECK: Only include background ref if mode is CHANGE
    if (config.backgroundMode === 'change' && config.backgroundRefImage) {
        parts.push(await fileToPart(config.backgroundRefImage));
    }
    
    // STRICT CHECK: Only include prop refs if mode is CHANGE
    if (config.propMode === 'change') {
        if (config.propRefImages && config.propRefImages.length > 0) {
            for (const img of config.propRefImages) {
                parts.push(await fileToPart(img));
            }
        } else if (config.propRefImage) {
            // Fallback for backward compatibility
            parts.push(await fileToPart(config.propRefImage));
        }
    }

    let assignedPose = "";
    if (config.poseCategory && !config.poseDescription) {
        assignedPose = getRandomPose(config.poseCategory);
    }

    const prompt = buildPrompt(config, '', assignedPose);
    parts.push({ text: prompt });

    // --- ASPECT RATIO LOGIC ---
    let imageConfig: any = { 
        imageSize: getResolutionForPlan(plan, config.outputResolution) 
    };

    if (config.aspectRatioMode === 'change' && config.aspectRatio) {
        imageConfig.aspectRatio = config.aspectRatio;
    }

    console.log(`[Gemini] Generating Reshoot at ${imageConfig.imageSize} (${plan} plan). AspectRatioMode: ${config.aspectRatioMode}`);

    const response = await retryWithBackoff<GenerateContentResponse>(() => ai.models.generateContent({
      model: getActiveModel(),
      contents: { parts: parts },
      config: {
        imageConfig: imageConfig,
        safetySettings: [
            { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
        ]
      }
    }));

    if (response.candidates && response.candidates[0]) {
        let result = handleGenerationCandidate(response.candidates[0]);
        // Watermark removed for Trial plan
        return result;
    }
    
    throw new Error("The API returned no candidates. Please check your internet connection.");

  } catch (error) {
    console.error("Gemini API Error:", error);
    try {
        if (localStorage) {
             localStorage.removeItem('gemini_fallback_ts');
        }
    } catch (e) {}
    throw error;
  }
};

export const generateAssetImage = async (
    prompt: string,
    type: 'background' | 'prop',
    plan: string = 'trial',
    resolutionOverride?: '1K' | '2K' | '4K'
): Promise<string> => {
    let fullPrompt = "";
    let aspectRatio = "1:1";

    if (type === 'background') {
        fullPrompt = `Generate a high-quality background image: "${prompt}". 
        Style: Professional studio photography or 3D render. 
        Lighting: Cinematic. 
        NO PEOPLE. NO TEXT. Pure background texture/scene. Ensure NO lighting gear is visible.`;
        aspectRatio = "16:9";
    } else {
        fullPrompt = `Generate a high-quality object image on a transparent or solid white background: "${prompt}". 
        Style: Product photography. 
        Lighting: Studio strobe. 
        NO PEOPLE. NO TEXT. Focus on the object. Ensure NO lighting gear is visible.`;
        aspectRatio = "1:1";
    }

    const resolution = getResolutionForPlan(plan, resolutionOverride);

    const response = await retryWithBackoff<GenerateContentResponse>(() => ai.models.generateContent({
        model: getActiveModel(),
        contents: { parts: [{ text: fullPrompt }] },
        config: {
            imageConfig: { aspectRatio: aspectRatio as any, imageSize: resolution },
            safetySettings: [
                { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
            ]
        }
    }));

    if (response.candidates && response.candidates[0]) {
        return handleGenerationCandidate(response.candidates[0]);
    }
    throw new Error("Asset generation failed.");
};

export const generateFlatLayAsset = async (
    refImage: File,
    plan: string = 'trial',
    resolutionOverride?: '1K' | '2K' | '4K'
): Promise<string> => {
    const parts = [
        await fileToPart(refImage),
        { text: `Extract the main object from this image and place it on a clean white background. 
                 Convert it into a professional product photography sticker/asset. 
                 Remove all background clutter. Keep the object only. Ensure NO lighting gear or modifiers are visible.` }
    ];

    const resolution = getResolutionForPlan(plan, resolutionOverride);

    const response = await retryWithBackoff<GenerateContentResponse>(() => ai.models.generateContent({
        model: getActiveModel(),
        contents: { parts: parts },
        config: {
            imageConfig: { aspectRatio: '1:1', imageSize: resolution },
            safetySettings: [
                { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
                { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
            ]
        }
    }));

    if (response.candidates && response.candidates[0]) {
        return handleGenerationCandidate(response.candidates[0]);
    }
    throw new Error("Flat lay generation failed.");
};

export const generateVariations = async (
    mainImage: File,
    config: TransformationConfig,
    count: number = 3,
    plan: string = 'trial',
    randomize: boolean = false
): Promise<string[]> => {
    // Note: Gemini doesn't support 'n' count natively in generateContent for images yet in this SDK version 
    // without multiple calls or specific bulk endpoints.
    // For now, we will loop. In a production backend, this would be parallelized.
    
    const results: string[] = [];
    
    // Arrays for randomization
    const angles = ['High Angle', 'Low Angle', 'Dutch Angle', 'Straight On', 'Side Profile', 'Top-down'];
    const framings = ['Closeup', 'Medium Shot', 'Full Body', 'Wide Shot', 'Extreme Closeup'];
    
    // EXCLUDE FAMILY AND BABY FROM RANDOM VARIATIONS
    const poseCategories = Object.keys(POSE_LIBRARY).filter(cat => cat !== 'FAMILY' && cat !== 'BABY');

    // Limit to 1 for demo speed if needed, or loop
    for (let i = 0; i < count; i++) {
        try {
            let localConfig = { ...config };
            let promptSuffix = ` Variation ${i + 1}.`;

            if (randomize) {
                // Random Angle
                localConfig.angle = angles[Math.floor(Math.random() * angles.length)];
                localConfig.angleMode = 'change';
                
                // Random Framing
                localConfig.framing = framings[Math.floor(Math.random() * framings.length)];
                localConfig.framingMode = 'change';

                // Random Pose
                const randomCat = poseCategories[Math.floor(Math.random() * poseCategories.length)];
                const randomPose = POSE_LIBRARY[randomCat][Math.floor(Math.random() * POSE_LIBRARY[randomCat].length)];
                localConfig.poseDescription = randomPose;
                
                promptSuffix += ` RANDOMIZED VARIATION: Change the camera angle to ${localConfig.angle} and the framing to ${localConfig.framing}. Use a new pose: ${randomPose}. Ensure this image looks visibly different from the original and other variations. ABSOLUTELY NO LIGHTING GEAR VISIBLE.`;
                console.log(`[Gemini] Generating randomized variation ${i+1}: ${localConfig.angle}, ${localConfig.framing}`);
            } else {
                 promptSuffix += ` Slightly different lighting angle or pose nuance. Ensure this variation looks distinct and contains NO visible lighting equipment.`;
            }

            const parts: any[] = [];
            parts.push(await fileToPart(mainImage));
            
            // Re-add refs if needed and not randomized (or if randomized but still valid)
            if (localConfig.backgroundMode === 'change' && localConfig.backgroundRefImage) {
                 parts.push(await fileToPart(localConfig.backgroundRefImage));
            }
            
            const basePrompt = buildPrompt(localConfig, promptSuffix);
            parts.push({ text: basePrompt });

            // --- ASPECT RATIO LOGIC FOR VARIATIONS ---
            let imageConfig: any = { 
                imageSize: getResolutionForPlan(plan, localConfig.outputResolution) 
            };

            if (localConfig.aspectRatioMode === 'change' && localConfig.aspectRatio) {
                imageConfig.aspectRatio = localConfig.aspectRatio;
            }

            const response = await retryWithBackoff<GenerateContentResponse>(() => ai.models.generateContent({
                model: getActiveModel(),
                contents: { parts: parts },
                config: {
                    imageConfig: imageConfig,
                    // Increase temperature to encourage variety
                    temperature: 1.2,
                    safetySettings: [
                        { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
                        { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
                        { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
                        { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
                    ]
                }
            }));

            if (response.candidates && response.candidates[0]) {
                let result = handleGenerationCandidate(response.candidates[0]);
                // Watermark removed for Trial plan
                results.push(result);
            }
        } catch (e) {
            console.warn(`Variation ${i} failed`, e);
        }
    }
    return results;
};

// --- AUTO-TUNE (AI DIRECTOR) ---
// Analyzes the image and suggests a configuration
export const analyzeImageForConfig = async (image: File): Promise<{ config: Partial<TransformationConfig>, critique: string }> => {
    const parts = [
        await fileToPart(image),
        { text: `Analyze this image as a professional photography director.
                 1. Identify the subject type (Portrait, Product, Landscape, etc.).
                 2. Critique the current lighting, framing, and angle.
                 3. Suggest specific improvements to make it "Award Winning".
                 
                 Return a JSON object with this structure:
                 {
                    "critique": "A short, constructive critique of the original photo.",
                    "suggestedConfig": {
                        "lighting": "Studio Strobe" | "Diffused Strobe" | "Harsh" | "Ambient",
                        "framing": "Macro" | "Closeup" | "Medium" | "Wide",
                        "angle": "High" | "Low" | "Straight On" | "Tilted",
                        "retouching": "Light" | "Moderate" | "Extreme"
                    }
                 }
                 Only return JSON.` }
    ];

    try {
        const response = await retryWithBackoff<GenerateContentResponse>(() => ai.models.generateContent({
            model: 'gemini-2.5-flash', // Use faster model for text analysis
            contents: { parts: parts },
            config: {
                responseMimeType: 'application/json'
            }
        }));

        if (response.candidates && response.candidates[0]) {
            if (response.candidates[0].content && response.candidates[0].content.parts[0].text) {
                 const text = response.candidates[0].content.parts[0].text;
                 const data = JSON.parse(text);
                 return {
                     config: {
                         lighting: data.suggestedConfig?.lighting,
                         framing: data.suggestedConfig?.framing,
                         angle: data.suggestedConfig?.angle,
                         retouching: data.suggestedConfig?.retouching,
                         // Auto-set modes to change if a suggestion exists
                         framingMode: data.suggestedConfig?.framing ? 'change' : 'keep',
                         angleMode: data.suggestedConfig?.angle ? 'change' : 'keep'
                     },
                     critique: data.critique || "Optimization suggested."
                 };
            }
        }
        throw new Error("No analysis returned.");
    } catch (e) {
        console.error("Auto-tune failed", e);
        return { 
            config: { lighting: 'Studio Strobe', retouching: 'Moderate' }, 
            critique: "Could not analyze. Default studio settings applied." 
        };
    }
};
